import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, HStack, Switch, Text } from '@chakra-ui/react';

import { HorizontalTabs, HTab } from '../../../../components/HorizontalTabs';
import LoadingSpinner from '../../../../components/Spinner';
import CompanySettingTypes from '../../../../constants/companySettings';
import {
  getCompany,
  selectCompanySetting,
  updateCompanySetting
} from '../../../../redux/modules/company';
import {
  getAutomations,
  getTriggers,
  selectEmailAutomations,
  selectEmailAutomationTriggers
} from '../../../../redux/modules/emailAutomations';

import AutomationHistory from './AutomationHistory';
import AutomationSettings from './AutomationSettings';
import AutomationSidePanel from './AutomationSidePanel';

const sidePanelWidth = 250;

const EmailAutomationPanel = () => {
  const dispatch = useDispatch();
  const emailAutomations = useSelector(selectEmailAutomations);
  const triggers = useSelector(selectEmailAutomationTriggers);
  const emailAutomationEnabledSetting = useSelector(selectCompanySetting(CompanySettingTypes.EmailAutomationEnabled));
  const { companyId } = useParams();
  const [currentTabId, setCurrentTabId] = useState('settings');

  useEffect(() => {
    dispatch(getAutomations(companyId));
    dispatch(getTriggers());
  }, [companyId]);

  const templateTabOptions = useMemo(() => [
    {
      tabId: 'settings',
      title: 'Automation Settings',
      main: AutomationSettings,
      onTabSelect: () => setCurrentTabId('settings')
    },
    {
      tabId: 'sequences',
      title: 'Automation History',
      main: AutomationHistory,
      onTabSelect: () => setCurrentTabId('sequences')
    }]);

  useEffect(() => () => dispatch(getCompany(companyId)), []);

  const handleOnChange = settingTypeId => e => {
    if (!settingTypeId) {
      console.error('settingTypeId is not defined'); // eslint-disable-line no-console
      e.preventDefault();
      return;
    }

    dispatch(updateCompanySetting(
      companyId,
      settingTypeId,
      e.target.checked,
      e.target.checked
    ));
  };

  if (!(emailAutomations && triggers)) {
    return <LoadingSpinner />;
  }

  if (emailAutomationEnabledSetting.isEnabled) {
    return (
      <HorizontalTabs
        extendDefaultContentBoxStyles
        contentBoxStyles={{ ml: 4, height: '92%' }}
        defaultTabId={currentTabId}
        paddingLeft={sidePanelWidth + 100}
        sidePanel={<AutomationSidePanel w={sidePanelWidth} />}
        tabWidth={200}
      >
        {templateTabOptions.map(option => (
          <HTab {...option} key={option.tabId} />
        ))}
      </HorizontalTabs>
    );
  }

  return (
    <Box layerStyle="tabPanel">
      <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
        <HStack justifyContent="space-between">
          <Box pr={4} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Enable Email Automation</Text>
            <Text color="greyScale.500" textStyle="bodySm">Email automation allows you to set automated email rules to notify subjects about upcoming/past events.</Text>
            <Text color="greyScale.500" textStyle="bodySm">Currently supported: policy expiration</Text>
          </Box>
          <Box>
            <Switch
              isChecked={emailAutomationEnabledSetting?.isEnabled}
              onChange={handleOnChange(emailAutomationEnabledSetting?.id)}
            />
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

export default EmailAutomationPanel;
